import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import TrainingProgress from './TrainingProgress';
import WidgetPersonaTab from './admin/WidgetPersonaTab';
import AnalyticsTab from './admin/AnalyticsTab';
import SourcesTab from './admin/SourcesTab';
import GuideTab from './admin/GuideTab';
import API_BASE_URL from '../config';

const fontStack = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: '0.9rem',
  fontFamily: fontStack,
  borderRadius: '8px',
  border: '1px solid #eee',
  background: '#fafafa',
  color: '#1a1a1a',
  outline: 'none',
  boxSizing: 'border-box',
  marginBottom: '12px',
};

const btnPrimary = {
  padding: '10px 24px',
  fontSize: '0.875rem',
  fontFamily: fontStack,
  fontWeight: 500,
  backgroundColor: '#111',
  color: '#fff',
  border: 'none',
  borderRadius: '8px',
  cursor: 'pointer',
  transition: 'opacity 0.15s ease',
};

const TABS = [
  { key: 'training', label: 'Training' },
  { key: 'widget', label: 'Widget & Persona' },
  { key: 'analytics', label: 'Analytics' },
  { key: 'sources', label: 'Sources' },
  { key: 'guide', label: 'Guide' },
];

function AdminView() {
  const navigate = useNavigate();
  const { channelUrl, customerKey: authCustomerKey, logout } = useAuth();
  const [tab, setTab] = React.useState('training');
  const [message, setMessage] = React.useState('');
  const [videoTrainMessage, setVideoTrainMessage] = React.useState('');
  const [customerKey, setCustomerKey] = React.useState(authCustomerKey);
  const [trainingVideoIds, setTrainingVideoIds] = React.useState([]);
  const [isTraining, setIsTraining] = React.useState(false);
  const [forceReprocess, setForceReprocess] = React.useState(false);
  const [pdfFiles, setPdfFiles] = React.useState([]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setMessage('');
    setIsTraining(true);
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/train`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ 
          channel_url: channelUrl, 
          customer_key: customerKey,
          force: forceReprocess
        }),
      });
      const data = await response.json();

      if (!response.ok || data.error) {
        setMessage(`Error: ${data.error || `training request failed (HTTP ${response.status})`}`);
        setIsTraining(false);
        return;
      }

      if (data.video_ids && data.video_ids.length > 0) {
        setTrainingVideoIds(data.video_ids);
        setMessage(data.message || `Training started for ${data.video_ids.length} video(s)`);
      } else {
        setMessage(data.message || 'Training started!');
        setIsTraining(false);
      }
    } catch (error) {
      setMessage('Error occurred during training. Please try again.');
      setIsTraining(false);
    }
  };

  const handleVideoSubmit = async (event) => {
    event.preventDefault();
    const videoUrl = event.target.videoURL.value;
    setVideoTrainMessage('');
    setIsTraining(true);

    try {
      const formData = new FormData();
      formData.append('channel_url', channelUrl);
      formData.append('video_url', videoUrl);
      formData.append('customer_key', customerKey);
      if (forceReprocess) formData.append('force', 'true');
      pdfFiles.forEach(f => formData.append('pdfs', f));

      const response = await fetch(`${API_BASE_URL}/api/train_video`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });
      const data = await response.json();

      if (!response.ok || data.error) {
        setVideoTrainMessage(`Error: ${data.error || `training request failed (HTTP ${response.status})`}`);
        setIsTraining(false);
        return;
      }

      if (data.video_id) {
        setTrainingVideoIds([data.video_id]);
        setVideoTrainMessage(data.message || 'Video training started!');
      } else {
        setVideoTrainMessage(data.message || 'Video training started!');
        setIsTraining(false);
      }

      event.target.videoURL.value = '';
      setPdfFiles([]);
    } catch (error) {
      setVideoTrainMessage('Error occurred during video training. Please try again.');
      setIsTraining(false);
    }
  };

  const handleTrainingComplete = (statusData) => {
    setIsTraining(false);
    console.log('Training completed:', statusData);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  return (
    <div style={{
      fontFamily: fontStack,
      maxWidth: tab === 'training' || tab === 'guide' ? '820px' : '1100px',
      margin: '0 auto',
      padding: '48px 20px 80px',
      color: '#1a1a1a',
      background: '#fff',
      minHeight: '100vh',
    }}>
      {/* Header */}
      <header style={{ textAlign: 'center', marginBottom: '40px' }}>
        <img
          src="/inquiros-logo.png"
          alt="Inquiros"
          style={{ height: '48px', display: 'block', margin: '0 auto' }}
        />
        <p style={{ fontSize: '0.95rem', color: '#888', marginTop: '6px', fontWeight: 400 }}>
          Admin
        </p>
      </header>

      {/* Nav */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '36px' }}>
        <button
          onClick={() => navigate('/')}
          style={{ background: 'none', border: 'none', color: '#888', fontSize: '0.85rem', cursor: 'pointer', padding: '4px 0', fontFamily: fontStack }}
        >
          &larr; Back to search
        </button>
        <button
          onClick={handleLogout}
          style={{ background: 'none', border: '1px solid #ddd', color: '#666', fontSize: '0.8rem', padding: '6px 14px', borderRadius: '6px', cursor: 'pointer', fontFamily: fontStack }}
        >
          Logout
        </button>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Admin sections"
           style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e5e5', marginBottom: 28 }}>
        {TABS.map((t) => (
          <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)}
                  style={{
                    padding: '9px 18px', fontSize: '0.85rem', fontFamily: fontStack, cursor: 'pointer',
                    border: 'none', borderBottom: tab === t.key ? '2px solid #111' : '2px solid transparent',
                    background: 'none', color: tab === t.key ? '#111' : '#888',
                    fontWeight: tab === t.key ? 600 : 400,
                  }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'widget' && <WidgetPersonaTab />}
      {tab === 'guide' && <GuideTab />}
      {tab === 'sources' && <SourcesTab />}
      {tab === 'analytics' && <AnalyticsTab corpusId={channelUrl} customerKey={customerKey} />}

      {/* Train Channel */}
      <section hidden={tab !== 'training'} style={{ marginBottom: '32px', padding: '24px', border: '1px solid #eee', borderRadius: '12px' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '20px', color: '#1a1a1a' }}>
          Train on Channel
        </h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
          <input
            name="channelURL"
            type="text"
            value={channelUrl}
            readOnly
            style={{ ...inputStyle, background: '#f5f5f5', color: '#888' }}
          />
          <input
            name="customerKey"
            type="text"
            value={customerKey}
            onChange={e => setCustomerKey(e.target.value)}
            placeholder="Customer key"
            style={inputStyle}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              id="forceReprocess"
              checked={forceReprocess}
              onChange={e => setForceReprocess(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#111' }}
            />
            <span style={{ fontSize: '0.85rem', color: '#666' }}>
              Force reprocess all videos
            </span>
          </label>
          <button type="submit" style={btnPrimary}>
            Train Model
          </button>
        </form>
        {message && (
          <p style={{ marginTop: '14px', textAlign: 'center', fontSize: '0.875rem', color: message.includes('Error') ? '#dc2626' : '#1a1a1a' }}>
            {message}
          </p>
        )}
      </section>

      {/* Train Single Video */}
      <section hidden={tab !== 'training'} style={{ marginBottom: '32px', padding: '24px', border: '1px solid #eee', borderRadius: '12px' }}>
        <h2 style={{ fontSize: '1.1rem', fontWeight: 600, marginBottom: '20px', color: '#1a1a1a' }}>
          Add Video
        </h2>
        <form onSubmit={handleVideoSubmit} style={{ display: 'flex', flexDirection: 'column' }}>
          <input
            name="channelURL"
            type="text"
            value={channelUrl}
            readOnly
            style={{ ...inputStyle, background: '#f5f5f5', color: '#888' }}
          />
          <input
            name="customerKey"
            type="text"
            value={customerKey}
            onChange={e => setCustomerKey(e.target.value)}
            placeholder="Customer key"
            style={inputStyle}
          />
          <input
            name="videoURL"
            type="text"
            placeholder="Video URL"
            style={inputStyle}
          />
          <div style={{ marginBottom: '16px' }}>
            <label style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginBottom: '6px' }}>
              Attach PDFs (optional)
            </label>
            <input
              type="file"
              multiple
              accept=".pdf"
              onChange={e => setPdfFiles(Array.from(e.target.files))}
              style={{ fontSize: '0.85rem', fontFamily: fontStack }}
            />
            {pdfFiles.length > 0 && (
              <div style={{ marginTop: '6px', fontSize: '0.8rem', color: '#888' }}>
                {pdfFiles.map((f, i) => <div key={i}>{f.name}</div>)}
              </div>
            )}
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={forceReprocess}
              onChange={e => setForceReprocess(e.target.checked)}
              style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#111' }}
            />
            <span style={{ fontSize: '0.85rem', color: '#666' }}>
              Force reprocess video
            </span>
          </label>
          <button type="submit" style={btnPrimary}>
            Train on Video
          </button>
        </form>
        {videoTrainMessage && (
          <p style={{ marginTop: '14px', textAlign: 'center', fontSize: '0.875rem', color: videoTrainMessage.includes('Error') ? '#dc2626' : '#1a1a1a' }}>
            {videoTrainMessage}
          </p>
        )}
      </section>

      {/* Training Progress */}
      {tab === 'training' && (isTraining || trainingVideoIds.length > 0) && customerKey && (
        <TrainingProgress
          channelUrl={channelUrl}
          customerKey={customerKey}
          videoIds={trainingVideoIds}
          onComplete={handleTrainingComplete}
        />
      )}
    </div>
  );
}

export default AdminView;
